/* GrowthOS — Opportunity form sections (used by addDeals.jsx for Create/Edit
   Opportunity). Same pattern as leadForm.jsx/contactForm.jsx/accountForm.jsx:
   section cards in a 2-col grid + a full-width notes card, config-driven
   `SECTIONS`, reusing the exact gos-cl-* / gos-dd-* classes — just the field
   groups match Opportunity's real schema instead of Lead's/Contact's/Account's.
   Left column: "Opportunity Information" (13 fields — the pipeline/deal-metrics
   core, kept as one card since splitting it added more header overhead than it
   saved). Right column: "Ownership & Source" + "Source Details" (balances the
   left column's height, same principle as Contact's/Account's layout).
   "Account Name"/"Campaign Source" are the two searchable fields (real lists can
   be large), backed by the existing PaginatedDropdown hook. "Contact Name" is a
   dependent dropdown — disabled until an Account is picked, options come from
   `contactOptions` (fetched by addDeals.jsx's fetchContactsByAccountId), same
   as the original dealForm.jsx. Adds a `date` field type (Closing Date) and a
   `value` override (Reach Out For Marketing's Yes/No label) on top of the
   Lead/Contact/Account renderField contract. */
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { getActiveOwners, getGenericMasterFilteredListByKey } from '../../redux/selector';
import { NameFormatter } from '../../shared/controls/formatters';
import PaginatedDropdown from '../../shared/component/PaginatedDropdown';
import apiServices from '../../services/service';
import { FIc } from "../../shared/component/GosIcon";


/* GrowthOS select (reuses .gos-dd-* dropdown styles); `searchable` adds a search box
   that calls onSearch(text) and an infinite-scroll list that calls onScroll() near
   the bottom — both map straight onto PaginatedDropdown's handleSearch/handleScroll. */
const Select = ({ value, placeholder, options, onChange, disabled, searchable, onSearch, onScroll, loading }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);
  const onListScroll = (e) => {
    if (!onScroll) return;
    const el = e.target;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 40) onScroll();
  };
  return (
    <div className="gos-dd" ref={ref}>
      <button type="button" className={'gos-dd-btn' + (open ? ' open' : '') + (value ? ' has-val' : '')} onClick={() => !disabled && setOpen((o) => !o)} disabled={disabled}>
        <span className="gos-dd-label">{value || placeholder || 'Select…'}</span>
        <FIc name="chevD" size={14} style={{ flex: 'none', color: 'var(--gos-text-3)' }} />
      </button>
      {open ? (
        <div className="gos-dd-pop">
          {searchable ? (
            <div className="gos-dd-search">
              <FIc name="search" size={14} />
              <input autoFocus placeholder="Search…" onChange={(e) => onSearch && onSearch(e.target.value)} />
            </div>
          ) : null}
          <div className="gos-dd-list" onScroll={onScroll ? onListScroll : undefined}>
            {loading && options.length === 0 ? <div className="gos-dd-empty">Loading…</div> : null}
            {!loading && options.length === 0 ? <div className="gos-dd-empty">No options</div> : null}
            {options.map((o) => (
              <button key={o.value} type="button" className={'gos-dd-item' + (o.label === value ? ' on' : '')} onClick={() => { onChange(o); setOpen(false); }}>
                <span className="gos-dd-grow">{o.label}</span>
                {o.label === value ? <FIc name="check" size={14} style={{ color: 'var(--gos-accent)' }} /> : null}
              </button>
            ))}
            {loading && options.length > 0 ? <div className="gos-dd-empty">Loading more…</div> : null}
          </div>
        </div>
      ) : null}
    </div>
  );
};

const mapBoolToYesNo = (v) => (v === true ? 'Yes' : v === false ? 'No' : '');

const DealForm = ({ formData, handleChangeForm, handleChange, errormsg, refs, contactOptions = [] }) => {
  const owner = useSelector(getActiveOwners);
  const industryTypeDropdown = useSelector(getGenericMasterFilteredListByKey('Industry'));
  const leadSourceDropdown = useSelector(getGenericMasterFilteredListByKey('LeadSource'));
  const stageDropdown = useSelector(getGenericMasterFilteredListByKey('Stage'));
  const projectTypeDropdown = useSelector(getGenericMasterFilteredListByKey('ProjectTypeCrm'));
  const forecastCategoryDropdown = useSelector(getGenericMasterFilteredListByKey('ForecastCategory'));

  const {
    options: accounts,
    isLoading: isAccountLoading,
    handleSearch: handleAccountSearch,
    handleScroll: handleAccountScroll,
  } = PaginatedDropdown({
    fetchFn: apiServices.getAccountsDropdown,
    responseKey: 'accountList',
  });

  const {
    options: campaigns,
    isLoading: isCampaignLoading,
    handleSearch: handleCampaignSearch,
    handleScroll: handleCampaignScroll,
  } = PaginatedDropdown({
    fetchFn: apiServices.getCampaignDropdown,
    responseKey: 'campaignList',
  });

  const opt = (list, vKey, lKey, fmt) =>
    (list || []).map((o) => ({ value: o[vKey], label: fmt ? fmt(o[lKey]) : o[lKey] }));

  const REACH_OUT_OPTIONS = [
    { value: true, label: 'Yes' },
    { value: false, label: 'No' },
  ];

  // field config: text | select | select-search | date | textarea
  const SECTIONS = [
    {
      id: 'opportunity', icon: 'deal', title: 'Opportunity Information', sub: 'Deal details & pipeline',
      fields: [
        { label: 'Opportunity Name', name: 'name', req: true, ref: 'name', full: true },
        { label: 'Account Name', name: 'accountId', type: 'select-search', display: 'account', req: true, ref: 'accountId',
          options: (accounts || []).map((a) => ({ value: a.id, label: a.name })), loading: isAccountLoading,
          onSearch: handleAccountSearch, onScroll: handleAccountScroll },
        { label: 'Contact Name', name: 'contactId', type: 'select', display: 'contact', req: true, ref: 'contactId',
          disabled: !formData?.accountId, options: (contactOptions || []).map((c) => ({ value: c.id, label: c.name })) },
        { label: 'Project Type', name: 'projectTypeId', type: 'select', display: 'projectType', options: opt(projectTypeDropdown, 'id', 'displayName') },
        { label: 'Next Step', name: 'nextStep', req: true, ref: 'nextStep' },
        { label: 'Forecast Category', name: 'forecastCategoryId', type: 'select', display: 'forecastCategory', options: opt(forecastCategoryDropdown, 'id', 'displayName') },
        { label: 'Amount', name: 'amount' },
        { label: 'Stage', name: 'stageId', type: 'select', display: 'stage', req: true, ref: 'stageId', options: opt(stageDropdown, 'id', 'displayName') },
        { label: 'Probability', name: 'probability' },
        { label: 'Closing Date', name: 'closingDate', type: 'date' },
        { label: 'Expected Revenue', name: 'expectedRevenue' },
        { label: 'Campaign Source', name: 'campaignSourceId', type: 'select-search', display: 'campaignSource',
          options: (campaigns || []).map((c) => ({ value: c.id, label: c.campaignName })), loading: isCampaignLoading,
          onSearch: handleCampaignSearch, onScroll: handleCampaignScroll },
        { label: 'Reach Out for Marketing', name: 'reachOutForMarketingId', type: 'select',
          value: mapBoolToYesNo(formData?.reachOutForMarketing), options: REACH_OUT_OPTIONS },
      ],
    },
    {
      id: 'ownership', icon: 'user', title: 'Ownership & Source', sub: 'Owner, industry & origin',
      fields: [
        { label: 'Opportunity Owner', name: 'ownerId', type: 'select', display: 'owner', options: opt(owner, 'userId', 'display_name', NameFormatter) },
        { label: 'Industry', name: 'industryId', type: 'select', display: 'industry', req: true, ref: 'industryId', options: opt(industryTypeDropdown, 'id', 'displayName') },
        { label: 'Business Type', name: 'businessType' },
        { label: 'Lead Source', name: 'leadSourceId', type: 'select', display: 'leadSource', req: true, ref: 'leadSourceId', options: opt(leadSourceDropdown, 'id', 'displayName') },
      ],
    },
    {
      id: 'source', icon: 'target', title: 'Source Details', sub: 'Attribution',
      fields: [
        { label: 'Source Name', name: 'sourceName', req: true, ref: 'sourceName' },
        { label: 'Other Source Name', name: 'otherSourceName' },
        { label: 'Line Of Business', name: 'lineOfBusiness' },
      ],
    },
    {
      id: 'notes', icon: 'template', title: 'Opportunity Details', sub: 'Notes for the team',
      fields: [
        { label: 'Description', name: 'description', type: 'textarea', full: true },
        { label: 'Technology Stack', name: 'technologyStack', type: 'textarea', full: true },
      ],
    },
  ];

  const renderField = (fl) => {
    const err = errormsg?.[fl.name];
    let control;
    if (fl.type === 'select' || fl.type === 'select-search') {
      control = (
        <Select
          value={fl.value !== undefined ? fl.value : formData?.[fl.display] || ''}
          placeholder={'Select ' + fl.label}
          options={fl.options}
          onChange={(o) => handleChange(o, fl.name)}
          disabled={fl.disabled}
          searchable={fl.type === 'select-search'}
          onSearch={fl.onSearch}
          onScroll={fl.onScroll}
          loading={fl.loading}
        />
      );
    } else if (fl.type === 'date') {
      control = (
        <input
          type="date"
          className={'gos-cl-input' + (err ? ' err' : '')}
          name={fl.name}
          value={formData?.[fl.name] ? String(formData[fl.name]).substring(0, 10) : ''}
          onChange={handleChangeForm}
        />
      );
    } else if (fl.type === 'textarea') {
      control = (
        <textarea
          className={'gos-cl-input gos-cl-textarea' + (err ? ' err' : '')}
          name={fl.name}
          rows={4}
          placeholder={fl.ph || 'Enter ' + fl.label}
          value={formData?.[fl.name] ?? ''}
          onChange={handleChangeForm}
        />
      );
    } else {
      control = (
        <input
          className={'gos-cl-input' + (err ? ' err' : '')}
          name={fl.name}
          placeholder={fl.ph || 'Enter ' + fl.label}
          value={formData?.[fl.name] ?? ''}
          onChange={handleChangeForm}
        />
      );
    }
    return (
      <div className={'gos-cl-field' + (fl.full ? ' gos-cl-field-full' : '')} key={fl.name} ref={fl.ref ? refs?.[fl.ref] : undefined}>
        <label className="gos-cl-label">
          {fl.label}
          {fl.req ? <span className="gos-cl-req">*</span> : null}
        </label>
        {control}
        {err ? <span className="gos-cl-err">{err}</span> : null}
      </div>
    );
  };

  const secCount = (s) => s.fields.filter((fl) => { const v = fl.value !== undefined ? fl.value : formData?.[fl.name]; return v !== null && v !== undefined && String(v).trim() !== ''; }).length;

  const section = (s) => {
    const n = secCount(s);
    const total = s.fields.length;
    return (
      <div className="gos-lr-card gos-cl-sec" id={'sec-' + s.id} key={s.id}>
        <div className="gos-cl-sec-head">
          <span className="gos-cl-sec-ic"><FIc name={s.icon} size={17} /></span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="gos-cl-sec-title">{s.title}</div>
            <div className="gos-cl-sec-sub">{s.sub}</div>
          </div>
          <span className={'gos-cl-count' + (n === total && n > 0 ? ' done' : '')}>{n}/{total}</span>
        </div>
        <div className="gos-cl-sec-body">
          {s.fields.map(renderField)}
        </div>
      </div>
    );
  };

  const byId = (id) => SECTIONS.find((s) => s.id === id);

  return (
    <>
      <div className="gos-cl3-grid">
        <div className="gos-cl3-col">{section(byId('opportunity'))}</div>
        <div className="gos-cl3-col">{section(byId('ownership'))}{section(byId('source'))}</div>
      </div>
      <div style={{ marginTop: 18 }}>{section(byId('notes'))}</div>
    </>
  );
};

export default DealForm;
